// TMOS Objective Card Component
// Date: January 13, 2026
// Purpose: Card view for Plan Objective entities

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FlagIcon, EyeIcon, PencilIcon } from '@heroicons/react/24/outline';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { ProgressBar } from './ProgressBar';
import { PlanObjective } from '@/types/plan';
import { ObjectiveProgress } from '@/types/businessPerformance';

export interface ObjectiveCardProps {
  objective: PlanObjective;
  progress?: ObjectiveProgress;
  onView?: (objective: PlanObjective) => void;
  onEdit?: (objective: PlanObjective) => void;
  onClick?: (objectiveId: string) => void;
}

export const ObjectiveCard: React.FC<ObjectiveCardProps> = ({
  objective,
  progress,
  onView,
  onEdit,
  onClick
}) => {
  const navigate = useNavigate();

  const handleClick = () => {
    if (onClick) {
      onClick(objective.id);
    } else if (onView) {
      onView(objective);
    } else {
      navigate(`/performance/objectives/${objective.id}`);
    }
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (onEdit) {
      onEdit(objective);
    } else {
      navigate(`/performance/objectives/${objective.id}/edit`);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'HIGH':
        return 'bg-red-100 text-red-800';
      case 'MEDIUM':
        return 'bg-yellow-100 text-yellow-800';
      case 'LOW':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'bg-blue-100 text-blue-800';
      case 'COMPLETED':
        return 'bg-green-100 text-green-800';
      case 'ON_HOLD':
        return 'bg-yellow-100 text-yellow-800';
      case 'CANCELLED':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getProgressColor = (status?: string) => {
    switch (status) {
      case 'ON_TRACK':
        return 'success';
      case 'AT_RISK':
        return 'warning';
      case 'OFF_TRACK':
        return 'danger';
      default:
        return 'primary';
    }
  };

  return (
    <Card
      variant="outline"
      padding="none"
      className="hover:shadow-md transition-shadow cursor-pointer h-full flex flex-col"
      onClick={handleClick}
    >
      <CardContent className="p-4 flex-1 flex flex-col">
        {/* Header: Priority and Status */}
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <FlagIcon className="w-5 h-5 text-[var(--color-info)] flex-shrink-0" />
            <Badge className={`text-xs ${getPriorityColor(objective.priority)}`}>
              {objective.priority}
            </Badge>
            <Badge className={`text-xs ${getStatusColor(objective.status)}`}>
              {objective.status}
            </Badge>
          </div>
        </div>

        {/* Name */}
        <h3 className="font-semibold text-[var(--color-secondary-text)] truncate mb-2">
          {objective.name}
        </h3>

        {/* Description */}
        {objective.description && (
          <p className="text-sm text-[var(--color-secondary-text)] opacity-75 line-clamp-2 mb-3">
            {objective.description}
          </p>
        )}

        {/* Target vs Actual */}
        {progress && (
          <div className="mb-3 flex-1">
            <div className="flex justify-between items-center mb-1 text-xs text-[var(--color-secondary-text)]">
              <span>Target: {objective.target_value} {objective.target_unit || ''}</span>
              {progress.current_actual !== null && (
                <span>Actual: {progress.current_actual.toFixed(2)} {objective.target_unit || ''}</span>
              )}
            </div>
            <ProgressBar
              progress={progress.progress_percent}
              color={getProgressColor(progress.status)}
              height="sm"
            />
            {progress.variance !== 0 && (
              <div className="text-xs text-[var(--color-secondary-text)] opacity-75 mt-1">
                Variance: {progress.variance > 0 ? '+' : ''}{progress.variance.toFixed(2)} ({progress.variance_percent > 0 ? '+' : ''}{progress.variance_percent.toFixed(1)}%)
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-200">
          {onView && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onView(objective);
              }}
              className="p-1 text-[var(--color-secondary-text)] hover:text-[var(--color-primary)] transition-colors"
              title="View"
            >
              <EyeIcon className="w-4 h-4" />
            </button>
          )}
          {onEdit && (
            <button
              onClick={handleEdit}
              className="p-1 text-[var(--color-secondary-text)] hover:text-[var(--color-primary)] transition-colors"
              title="Edit"
            >
              <PencilIcon className="w-4 h-4" />
            </button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

